/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Builder, builder, CustomCode } from "@builder.io/react";
import { BuilderContent } from "@builder.io/sdk";
import { getAsyncProps } from "@builder.io/utils";

import { BUILDER_API_KEY } from "../shared/constants";
import { ServerSidePropsContext, StaticPropsContext } from "../types/page";
import { BuilderConfig, BuilderModel } from "./types";

const bannerContext = require.context("../components", true, /\.builder-banner\.ts$/);
const componentContext = require.context("../components", true, /\.builder-component\.ts$/);
const messageContext = require.context("../components", true, /\.builder-message\.ts$/);

type BuilderConfigModule = { default: BuilderConfig<unknown> };

const builderBannerConfigs = bannerContext
    .keys()
    .map((key) => bannerContext<BuilderConfigModule>(key));
const builderComponentConfigs = componentContext
    .keys()
    .map((key) => componentContext<BuilderConfigModule>(key));
const builderMessageConfigs = messageContext
    .keys()
    .map((key) => messageContext<BuilderConfigModule>(key));

builder.init(BUILDER_API_KEY);

const allConfigs = [...builderComponentConfigs, ...builderBannerConfigs];

Builder.registerComponent(CustomCode, {
    name: "Custom Code",
    requiredPermissions: ["editCode"],
    inputs: [
        {
            name: "code",
            type: "code",
            // @ts-ignore - Builder typing is incorrect and this is valid
            options: {
                language: "html",
            },
            required: true,
            defaultValue: "<p>Hello there, I am custom HTML code!</p>",
            code: true,
        },
        {
            name: "replaceNodes",
            type: "boolean",
            helperText: "Preserve server rendered dom nodes",
            advanced: true,
        },
        {
            name: "scriptsClientOnly",
            type: "boolean",
            helperText:
                "Only print and run scripts on the client. Important when scripts influence DOM that could be replaced when client loads",
            advanced: true,
        },
    ],
});

const configsForModel = (model: BuilderModel): BuilderConfigModule[] => {
    switch (model) {
        case BuilderModel.PAGE:
            return builderComponentConfigs;
        case BuilderModel.BANNER:
            return builderBannerConfigs;
        case BuilderModel.MESSAGE:
            return builderMessageConfigs;
    }
};

const registerComponents = (configs) => {
    for (const exports of configs) {
        if (Object.hasOwnProperty.call(exports, "default")) {
            const { component, config } = exports.default;

            if (component && config) {
                Builder.registerComponent(component, config);
            }
        }
    }
};

export const resolveAsyncProps = (
    builderContent: BuilderContent,
    context: ServerSidePropsContext | StaticPropsContext
): Promise<BuilderContent> => {
    const asyncProps = allConfigs.reduce((accum, exports) => {
        if (Object.hasOwnProperty.call(exports, "default")) {
            const { asyncProps, config } = exports.default;

            if (asyncProps && config) {
                return {
                    ...accum,
                    [config.name]: (props) => asyncProps(context, props),
                };
            }
        }

        return accum;
    }, {});

    return getAsyncProps(builderContent, asyncProps);
};

export const init = (model: BuilderModel): void => {
    const configs = configsForModel(model);
    registerComponents(configs);
};
